import { useCallback } from "react";
import userService from "api/user/user.service";
import { mapUserDetailToRedux } from "api/user/user.type";
import { useAppDispatch } from "store";
import { setPermissionsError, setUser } from "store/commonSlice";

/**
 * Fetches `GET /v1/users/{id}` and writes the result into Redux — the one place the
 * app's `user.permissions` come from. Used by `router/AuthorizationPage` on every
 * route mount and by the Photoshoot Studio "Try again" state.
 *
 * Failure contract (`[PERMISSION_LOADING_FALLBACK]`): a 401/403/409/503 resolves with
 * `_handled` (`[HTTP_HANDLED_RESPONSE]`) and a 5xx/network error rejects — BOTH leave
 * `user.permissions` exactly as they were and raise `permissionsError`. The BE now
 * answers 503 `permissions.unavailable` when the permission read itself failed
 * (instead of a 200 carrying an all-disabled set, the 2026-09-02 dev/stage "Contact us"
 * incident), so an unresolved `permissions` + `permissionsError` is the "couldn't load,
 * retry" state `usePermissionState().isError` exposes. A later success clears the flag
 * through `setUser`.
 *
 * Resolves `true` when the user detail was written, `false` otherwise.
 */
const useUserDetailRefresh = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    async (userId: number | string): Promise<boolean> => {
      try {
        const res = await userService.getUserDetail({ user_id: String(userId) });
        if (res?._handled || !res?.responseData) {
          dispatch(setPermissionsError(true));

          return false;
        }

        dispatch(setUser(mapUserDetailToRedux(res.responseData)));

        return true;
      } catch {
        dispatch(setPermissionsError(true));

        return false;
      }
    },
    [dispatch]
  );
};

export default useUserDetailRefresh;
